// Capacity (PRD §3.9, CM1, CM3–CM5, CM7): every person's planned hours across all counted projects ÷
// their capacity, by week, month or quarter. The API sends raw weekly hours per assignment; this
// counts and adds them up in the browser, so status toggles, weighting and filters respond at once.

import type { Iso } from "./dates";
import { periodsOf, type Zoom } from "./estimate";
import { countsTowardCapacity, isClosed, type ProjectStatus } from "./status";

export interface CapacityInput {
  /** The Monday of each week in the window. */
  weekStarts: Iso[];
  /** Capacity per week, net of holidays, PTO-free working days and employment dates. */
  /** `archived`: the person is archived (EC11); the screen hides them unless asked. */
  people: { id: string; name: string; roleId?: string; capacity: number[]; archived?: boolean }[];
  projects: {
    id: string;
    code: string;
    name: string;
    clientName: string;
    status: ProjectStatus;
    closedOn?: Iso;
    probability: number;
    billable: boolean;
  }[];
  /** One per assignment, its hours aligned to `weekStarts`. A placeholder has no person. */
  rows: { projectId: string; personId: string | null; roleId: string; hours: number[] }[];
}

export interface CapacityOptions {
  /** Open statuses that count (CM3). Pending and booked by default (§3.10). */
  statuses: ReadonlySet<ProjectStatus>;
  /** Also count delivered, lost and cancelled projects in full: the history view. */
  closed: boolean;
  /** Weight prospect and opportunity hours by their probability. */
  weighted: boolean;
  billableOnly: boolean;
  /** Filters (CM4); absent means everything. */
  people?: ReadonlySet<string>;
  projects?: ReadonlySet<string>;
  clients?: ReadonlySet<string>;
  roles?: ReadonlySet<string>;
}

export interface CapacityCell {
  hours: number;
  capacity: number;
  billable: number;
  /** hours ÷ capacity; Infinity for hours with no capacity; null for neither. */
  load: number | null;
  contributors: { projectId: string; hours: number }[];
}

export interface CapacityRow {
  personId: string;
  name: string;
  cells: CapacityCell[];
  total: CapacityCell;
}

export interface CapacityView {
  periods: { key: string; label: string; weeks: number[] }[];
  people: CapacityRow[];
  /** Placeholder hours by role: demand nobody is named for yet. */
  unstaffed: { roleId: string; hours: number[] }[];
  /** CM5: billable hours ÷ capacity across the people shown, for the whole window. */
  billableUtilization: number | null;
  /** All hours ÷ all capacity across the people shown. */
  averageLoad: number | null;
}

const loadOf = (hours: number, capacity: number): number | null => (capacity > 0 ? hours / capacity : hours > 0 ? Infinity : null);

/** Does a row's project count in a given week under the options, and at what weight? */
export function rowWeight(p: CapacityInput["projects"][number], weekStart: Iso, o: CapacityOptions): number {
  if (o.billableOnly && !p.billable) return 0;
  if (o.projects && !o.projects.has(p.id)) return 0;
  if (o.clients && !o.clients.has(p.clientName)) return 0;
  const counts = (o.closed && isClosed(p.status)) || countsTowardCapacity(p, weekStart, o.statuses);
  if (!counts) return 0;
  return o.weighted && (p.status === "prospect" || p.status === "opportunity") ? p.probability : 1;
}

export function capacityView(input: CapacityInput, zoom: Zoom, o: CapacityOptions): CapacityView {
  const weeks = input.weekStarts.length;
  const periods = weeks ? periodsOf({ startMonday: input.weekStarts[0], weeks }, zoom) : [];
  const projects = new Map(input.projects.map((p) => [p.id, p]));

  // Counted hours per person per week, with what makes them up; placeholders by role.
  const byPerson = new Map<string, { hours: number[]; billable: number[]; contributors: Map<string, number[]> }>();
  const unstaffed = new Map<string, number[]>();
  for (const row of input.rows) {
    const p = projects.get(row.projectId);
    if (!p || (o.roles && !o.roles.has(row.roleId))) continue;
    if (row.personId && o.people && !o.people.has(row.personId)) continue;
    for (let w = 0; w < weeks; w++) {
      const h = row.hours[w] ?? 0;
      if (!h) continue;
      const weight = rowWeight(p, input.weekStarts[w], o);
      if (!weight) continue;
      const counted = h * weight;
      if (!row.personId) {
        const r = unstaffed.get(row.roleId) ?? new Array(weeks).fill(0);
        r[w] += counted;
        unstaffed.set(row.roleId, r);
        continue;
      }
      let entry = byPerson.get(row.personId);
      if (!entry) byPerson.set(row.personId, (entry = { hours: new Array(weeks).fill(0), billable: new Array(weeks).fill(0), contributors: new Map() }));
      entry.hours[w] += counted;
      if (p.billable) entry.billable[w] += counted;
      const mine = entry.contributors.get(p.id) ?? new Array(weeks).fill(0);
      mine[w] += counted;
      entry.contributors.set(p.id, mine);
    }
  }

  const cellOf = (weekIdx: number[], person: CapacityInput["people"][number], entry: ReturnType<typeof byPerson.get>): CapacityCell => {
    let hours = 0;
    let capacity = 0;
    let billable = 0;
    for (const w of weekIdx) {
      hours += entry?.hours[w] ?? 0;
      billable += entry?.billable[w] ?? 0;
      capacity += person.capacity[w] ?? 0;
    }
    const contributors = [...(entry?.contributors ?? new Map<string, number[]>())]
      .map(([projectId, h]) => ({ projectId, hours: weekIdx.reduce((s, w) => s + h[w], 0) }))
      .filter((c) => c.hours > 0)
      .sort((a, b) => b.hours - a.hours);
    return { hours, capacity, billable, load: loadOf(hours, capacity), contributors };
  };

  const all = Array.from({ length: weeks }, (_, w) => w);
  const people = input.people
    .filter((person) => !o.people || o.people.has(person.id))
    .filter((person) => person.capacity.some((c) => c > 0) || byPerson.has(person.id))
    .map((person) => {
      const entry = byPerson.get(person.id);
      return { personId: person.id, name: person.name, cells: periods.map((p) => cellOf(p.weeks, person, entry)), total: cellOf(all, person, entry) };
    });

  const capacity = people.reduce((s, r) => s + r.total.capacity, 0);
  const hours = people.reduce((s, r) => s + r.total.hours, 0);
  const billable = people.reduce((s, r) => s + r.total.billable, 0);
  return {
    periods,
    people,
    unstaffed: [...unstaffed].map(([roleId, h]) => ({ roleId, hours: h })).sort((a, b) => a.roleId.localeCompare(b.roleId)),
    billableUtilization: capacity > 0 ? billable / capacity : null,
    averageLoad: capacity > 0 ? hours / capacity : null,
  };
}
